import React, { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  FaExternalLinkAlt,
  FaFolderOpen,
  FaUpload,
} from "react-icons/fa";

import Chat from "./chat";
import Redesign from "./Redesign";
import Overview from "./Overview";
import ClientUpload from "./clientUploads";
import Previews from "./previews";
import Downloads from "./downloads";
import MillingEmail from "./MillingEmail";
import CaseLogs from "./CaseLogs";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getCasesById } from "../../store/common/casesSlice";
import UploadModal from "../../components/admin/UploadModal";
import TsUploadAccessBar from "../../components/admin/TsUploadAccessBar";
import { CaseType } from "../../types/Case";
import { smilePortalCaseHref } from "../../utils/linkedPortalCase";
import { Helmet } from "react-helmet-async";
import { isUrisBrand } from "../../utils/brandLogo";

type TabKey =
  | "overview"
  | "upload"
  | "chat"
  | "redesign"
  | "previews"
  | "downloads"
  | "milling"
  | "caseLogs";

const allTabs: Record<TabKey, { label: string; component: React.FC }> = {
  overview: { label: "Overview", component: Overview },
  upload: { label: "Client Upload", component: ClientUpload },
  chat: { label: "Chat", component: Chat },
  redesign: { label: "Redesign", component: Redesign },
  previews: { label: "Previews", component: Previews },
  downloads: { label: "Downloads", component: Downloads },
  milling: { label: "Milling Email", component: MillingEmail },
  caseLogs: { label: "Case Logs", component: CaseLogs },
};

const statusStyles = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "files pending")
    return "bg-red-50 text-red-700 ring-red-200";
  if (s === "submitted")
    return "bg-orange-50 text-orange-700 ring-orange-200";
  if (s === "query pending")
    return "bg-amber-50 text-amber-800 ring-amber-200";
  if (s === "under process")
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (s === "completed")
    return "bg-sky-50 text-sky-700 ring-sky-200";
  if (s.includes("milling"))
    return "bg-violet-50 text-violet-700 ring-violet-200";
  return "bg-gray-50 text-gray-600 ring-gray-200";
};

const urisStatusStyles = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "under process")
    return "bg-[#163226] text-[#7dffa8]";
  if (s === "submitted" || s.includes("milling"))
    return "bg-[#3a2e14] text-[#f0c36a]";
  if (s === "files pending" || s === "query pending")
    return "bg-[#3a1520] text-[#ff8b96]";
  return "bg-[#2a2b31] text-[#d0d2d8]";
};

const formatStatusLabel = (status: string) => {
  if (!status) return "—";
  if (status.toLowerCase() === "query pending") return "Query Pending";
  if (status.toLowerCase() === "under process") return "Under Process";
  return status;
};

const AdminCaseLayout: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as TabKey) || "overview";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const { user } = useAppSelector((state) => state.auth);
  const isUris = isUrisBrand(user);
  const { selectedCase, isLoading } = useAppSelector(
    (state) => state.adminCases,
  );
  const { access } = useAppSelector((state) => state.tsUploadAccess);
  const dispatch = useAppDispatch();
  const { case_id } = useParams<{ case_id: string }>();
  const [modalCase, setModalCase] = useState<CaseType | null>(null);

  useEffect(() => {
    if (case_id) dispatch(getCasesById(case_id));
  }, [dispatch, case_id, activeTab]);

  const tabs = useMemo(() => {
    const filteredTabs: Partial<typeof allTabs> = { ...allTabs };

    if (user?.role?.toLowerCase() !== "super admin") {
      delete filteredTabs.caseLogs;
    }

    return filteredTabs as Record<
      TabKey,
      { label: string; component: React.FC }
    >;
  }, [user]);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const ActiveComponent = useMemo(() => {
    if (!tabs[activeTab]) return Overview;
    return tabs[activeTab].component;
  }, [activeTab, tabs]);

  const handleUploadClick = (caseItem: CaseType) => setModalCase(caseItem);
  const handleCloseModal = () => setModalCase(null);

  const roleLower = user?.role?.toLowerCase() || "";
  const canViewBox = !["production", "admin read", "side admin"].includes(
    roleLower,
  );
  const canUploadTs = !["production", "admin read", "side admin"].includes(
    roleLower,
  );
  const statusLower = selectedCase?.status?.toLowerCase() || "";
  const crmBlockedByWorkflow =
    roleLower !== "super admin" &&
    access?.allowed !== true &&
    statusLower !== "completed";
  const uploadDisabled =
    !canUploadTs ||
    statusLower === "files pending" ||
    !selectedCase ||
    crmBlockedByWorkflow;

  const tsButtonClass = !canUploadTs
    ? isUris
      ? "cursor-not-allowed bg-[#2a2b31] text-[#6d6f76]"
      : "cursor-not-allowed bg-gray-300 text-gray-500"
    : crmBlockedByWorkflow
      ? "bg-amber-200 text-amber-800 cursor-not-allowed"
      : statusLower === "completed"
      ? "bg-gray-500 hover:bg-gray-600 text-white cursor-pointer"
      : statusLower === "files pending"
        ? "bg-red-300 text-white cursor-not-allowed"
        : "bg-[#2fa9bf] hover:bg-[#2296aa] text-white cursor-pointer";

  return (
    <>
      <Helmet>
        <title>
          {case_id ? `Case ${case_id}` : "Case Details"} · Admin
        </title>
      </Helmet>

      <div
        className={`flex flex-col gap-4 w-full min-w-0 text-sm ${
          isUris ? "uris-cases-scope text-[#c5c7cc]" : ""
        }`}
      >
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex flex-wrap items-center gap-2.5">
            <h1
              className={`text-lg sm:text-xl font-bold break-all ${
                isUris ? "text-white" : "text-[#4A4A48]"
              }`}
            >
              {case_id || "Case"}
            </h1>
            {selectedCase?.status ? (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium ${
                  isUris ? "" : "ring-1 ring-inset"
                } ${
                  isUris
                    ? urisStatusStyles(selectedCase.status)
                    : statusStyles(selectedCase.status)
                }`}
              >
                {formatStatusLabel(selectedCase.status)}
              </span>
            ) : isLoading ? (
              <span className={`text-sm ${isUris ? "text-[#8b8d93]" : "text-[#AAAAAA]"}`}>
                Loading…
              </span>
            ) : null}
            {selectedCase?.smile_id ? (
              <a
                href={smilePortalCaseHref(selectedCase.smile_id)}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm font-medium hover:underline break-all ${
                  isUris ? "text-[#ff8b96]" : "text-[#2fa9bf]"
                }`}
              >
                Smile case {selectedCase.smile_id}
              </a>
            ) : null}
            {selectedCase?.case_owner ? (
              <p className={`text-sm ${isUris ? "text-[#9a9ca3]" : "text-[#8A8A85]"}`}>
                Owner{" "}
                <span className={`font-medium ${isUris ? "text-white" : "text-[#4A4A48]"}`}>
                  {selectedCase.case_owner}
                </span>
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canViewBox && selectedCase?.box_folder_id ? (
              <a
                href={`https://app.box.com/folder/${selectedCase.box_folder_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:border-[#2fa9bf] hover:text-[#2fa9bf] transition text-xs font-medium"
              >
                <FaFolderOpen className="text-xs" />
                Box files
                <FaExternalLinkAlt className="text-[9px] opacity-70" />
              </a>
            ) : (
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs cursor-not-allowed ${
                  isUris
                    ? "border-[#2e3036] bg-[#141517] text-[#6d6f76]"
                    : "border-[#E8E8E4] bg-[#F8F8F6] text-[#AAAAAA]"
                }`}
              >
                <FaFolderOpen className="text-xs" />
                Box files
              </span>
            )}

            {selectedCase ? (
              <button
                type="button"
                onClick={() => handleUploadClick(selectedCase)}
                disabled={uploadDisabled}
                title={
                  crmBlockedByWorkflow
                    ? access?.message || "Waiting for Ready to Upload"
                    : undefined
                }
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition ${tsButtonClass}`}
              >
                <FaUpload className="text-[10px]" />
                TS Folder
              </button>
            ) : null}
            <TsUploadAccessBar caseId={case_id} />
          </div>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto">
          <div
            className={`flex gap-1 min-w-max border-b ${
              isUris ? "border-[#2e3036]" : "border-[#E8E8E4]"
            }`}
          >
            {Object.entries(tabs).map(([key, { label }]) => {
              const isActive = activeTab === key;
              const pending =
                label === "Chat"
                  ? selectedCase?.pending_comments_admin ?? 0
                  : 0;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabChange(key as TabKey)}
                  className={`relative px-3.5 py-2.5 text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? isUris
                        ? "text-white font-semibold"
                        : "text-[#2fa9bf] font-semibold"
                      : isUris
                        ? "text-[#8b8d93] hover:text-white"
                        : "text-[#8A8A85] hover:text-[#4A4A48]"
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {label}
                    {pending > 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold">
                        {pending}
                      </span>
                    ) : null}
                  </span>
                  {isActive ? (
                    <span
                      className={`absolute left-2 right-2 bottom-0 h-0.5 rounded-full ${
                        isUris ? "bg-[#c31c30]" : "bg-[#2fa9bf]"
                      }`}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {modalCase ? (
          <UploadModal
            caseId={modalCase.case_id}
            userId={user?.user_id.toString() || ""}
            boxFolderId={modalCase.box_folder_id.toString()}
            onClose={handleCloseModal}
          />
        ) : null}

        <div
          className={`rounded-2xl border shadow-sm min-h-[300px] p-3 sm:p-4 ${
            isUris
              ? "border-[#6b2430] bg-[#141517] shadow-none"
              : "border-[#E8E8E4] bg-white"
          }`}
        >
          <ActiveComponent />
        </div>
      </div>
    </>
  );
};

export default AdminCaseLayout;
